import { UserStatus } from '@/schema'
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

export const API_ENDPOINT_USER_MANAGEMENT = '/admin/v1/users'

export const QUERY_KEY_USER_LIST = 'user-management-list'
export const QUERY_KEY_USER_DETAIL = 'user-management-detail'

export const USER_LIST_QUERY_KEY = (params: (string | number | object)[]) => [
  QUERY_KEY_USER_LIST,
  ...(params ? [params] : []),
]

export const USER_DETAIL_QUERY_KEY = (id?: string) => [
  QUERY_KEY_USER_DETAIL,
  id,
]

export const USER_DETAIL_STALE_TIME = 5 * 60 * 1000

export const USER_STATUS_OPTIONS = [
  { label: 'Active', value: UserStatus.ACTIVE },
  { label: 'Pending', value: UserStatus.PENDING },
] as const

export type UserStatusLabel = 'Active' | 'Pending'

export const DEFAULT_SORT_COLUMN = 'full_name'
export const DEFAULT_SORT_DIRECTION = 'asc'
export const DEFAULT_SORT_BY = '+full_name'

export const MIN_ROLE_DROPDOWN_WIDTH = 140
export const MIN_STATUS_DROPDOWN_WIDTH = 140
export const MIN_ADD_BUTTON_WIDTH = 120

import { IAddUsersFormData, IInviteUser, TRole } from './types'

/**
 * Role types available in the system
 */
export enum ERoleType {
  ORGANIZATION_ADMIN = 'organization_admin', // Org-level admin with full access
  PROJECT_LEAD = 'project_lead', // Project management + review capabilities
  TEAM_MEMBER = 'team_member', // Project contributor
  REVIEWER = 'reviewer', // Document review access
  VIEWER = 'viewer', // Read-only access
}

/**
 * Stepper types available in the system
 */
export enum EStepperType {
  ADD_USERS = 'ADD_USERS',
  ASSIGNED_ACCESS = 'ASSIGNED_ACCESS',
}

/**
 * Field character limits
 */
export const FIELD_LIMITS = {
  /** Maximum length for first name and last name */
  NAME_MAX_LENGTH: 255,

  /** Maximum length for email address */
  EMAIL_MAX_LENGTH: 255,

  /** Maximum length for invitation message */
  MESSAGE_MAX_LENGTH: 32000,
} as const

/**
 * Search configuration
 */
export const SEARCH_CONFIG = {
  /** Minimum characters required to trigger search */
  MIN_SEARCH_LENGTH,

  /** Debounce delay for search input (milliseconds) */
  SEARCH_DEBOUNCE_MS: SEARCH_DEBOUNCE_DELAY_MS,
} as const

/**
 * Define which roles can invite which other roles
 */
export const ROLE_INVITE_PERMISSIONS: Record<TRole, ERoleType[]> = {
  [ERoleType.ORGANIZATION_ADMIN]: [
    ERoleType.ORGANIZATION_ADMIN,
    ERoleType.PROJECT_LEAD,
    ERoleType.TEAM_MEMBER,
  ],
  [ERoleType.PROJECT_LEAD]: [ERoleType.TEAM_MEMBER],
  [ERoleType.TEAM_MEMBER]: [],
}

/**
 * Display labels for all role types (used for UI display)
 */
const ROLE_DISPLAY_LABELS: Record<ERoleType, string> = {
  [ERoleType.ORGANIZATION_ADMIN]: 'Org Admin',
  [ERoleType.PROJECT_LEAD]: 'Project Lead',
  [ERoleType.TEAM_MEMBER]: 'Team Member',
  [ERoleType.REVIEWER]: 'Reviewer',
  [ERoleType.VIEWER]: 'Viewer',
}

/**
 * Get the display label for a role name.
 * Falls back to the raw role name if not found, or '-' if falsy.
 */
export const getRoleDisplayLabel = (roleName?: string): string => {
  if (!roleName) return '-'
  return ROLE_DISPLAY_LABELS[roleName as ERoleType] ?? roleName
}

/**
 * Get display labels for multiple roles, concatenated with commas.
 * Returns '-' if roles array is empty or undefined.
 */
export const getRolesDisplayLabel = (
  roles?: { role_name: string }[]
): string => {
  if (!roles || roles.length === 0) return '-'
  const labels = roles.map((role) => getRoleDisplayLabel(role.role_name))
  return Array.from(new Set(labels)).join(', ')
}

/**
 * Display labels for stepper types
 */
export const STEPPER_LABELS: Record<EStepperType, string> = {
  [EStepperType.ADD_USERS]: 'Add Users',
  [EStepperType.ASSIGNED_ACCESS]: 'Assigned Access',
}

/**
 * Empty add users row
 */
export const EMPTY_ADD_USERS_ROW: IInviteUser = {
  uuid: crypto.randomUUID(),
  firstName: '',
  lastName: '',
  email: '',
}

/**
 * Initial add users form data
 */
export const INITIAL_ADD_USERS_FORM_DATA: IAddUsersFormData = {
  users: [EMPTY_ADD_USERS_ROW],
  roleId: '',
  message: '',
  assignedProjects: [],
}
